import { tileSyncManager } from '../components/MapEditor/pluginSystem/plugins/virtualRoomPainter'
import { virtualRoomService, type VirtualRoomData } from './back4app/VirtualRoomService'
import { virtualRoomAreaBorderRenderer } from './VirtualRoomAreaBorderRenderer'
import { currentMapId } from '../utils/currentMapId'
import store from '../stores'
import type { TilePoint, VirtualRoomKind } from '../../../types/Maps'
import { DEFAULT_PROXIMITY_FALLBACK_PX } from '../constants/proximity'
import { requestTeleport } from './teleportService'
import { virtualRoomAreaDetectionService } from './VirtualRoomAreaDetectionService'
import { resolveVirtualRoomIdAtBody } from '../../../types/virtualRoomMembership'

const TILE_SIZE = 32
// Above this many px of pointer movement between down/up, treat the gesture as a camera drag,
// not a click — the room-name hover label is clickable but must never fire mid-pan (SC-396).
const HOVER_LABEL_CLICK_MOVE_THRESHOLD_PX = 6
// Teleport-via-hover-label requires a double-tap, not a single tap, so a stray click while
// panning/exploring the map never fires an unwanted teleport.
const DOUBLE_TAP_WINDOW_MS = 350
// Looser than the single-tap drag threshold since two separate taps land with more natural
// position variance than one continuous drag.
const DOUBLE_TAP_MOVE_THRESHOLD_PX = 12

/**
 * Virtual Room Area Data Structure
 */
export interface VirtualRoomArea {
  roomId: string
  roomName: string
  /** From the VR row; a cubicle shows no name tooltip. */
  kind: VirtualRoomKind
  color: string
  tiles: Array<{ x: number; y: number }>
  bounds: {
    minX: number
    minY: number
    maxX: number
    maxY: number
  }
  center: { x: number; y: number }
  tileCount: number
  properties: {
    uniqueRoomId: string
    proximityDistance: number
  }
  /** The VR row's metadata as stored, so fields like `spawnPoint` survive. */
  metadata?: {
    description?: string
    capacity?: number
    spawnPoint?: TilePoint
    [key: string]: unknown
  }
}

/**
 * Area Rendering Options
 */
export interface AreaRenderingOptions {
  alpha?: number
  strokeWidth?: number
  strokeColor?: string
  fillColor?: string
}

/**
 * How often a map editor room re-reads its map's VRs (SC-477; was 30 s). Each read costs a tile
 * query per 1,000 tiles plus one VR query, and only the 1-2 platform admins in an editor room poll.
 */
export const EDITOR_AREA_POLL_INTERVAL_MS = 120_000

/**
 * Regular rooms have no poll to repair a failed area read, so one reload is scheduled this long
 * after it.
 */
const AREA_LOAD_RETRY_DELAY_MS = 20_000

/**
 * Service Configuration
 */
export interface VirtualRoomAreaServiceConfig {
  enabled: boolean
  autoLoad: boolean
  renderOnLoad: boolean
  pollingInterval: number
  maxAreasPerRoom: number
  cacheDuration: number
  defaultRenderingOptions: AreaRenderingOptions
}

/**
 * Service State
 */
export interface VirtualRoomAreaServiceState {
  isInitialized: boolean
  isEnabled: boolean
  isRendering: boolean
  currentCustomRoomId: string | null
  /** VRs belong to the map, so they load by the room's map. */
  currentMapId: string | null
  loadedAreas: Map<string, VirtualRoomArea[]>
  renderingOptions: AreaRenderingOptions
  lastPollTime: number | null
  error: string | null
}

/**
 * Event Types for the service
 */
export enum VirtualRoomAreaEvent {
  AREAS_LOADED = 'areas_loaded',
  AREAS_RENDERED = 'areas_rendered',
  AREAS_HIDDEN = 'areas_hidden',
  ERROR = 'error',
  STATE_CHANGED = 'state_changed'
}

/**
 * Smart Virtual Room Area Service
 * 
 * This service provides a modular and scalable way to:
 * - Download virtual room areas for the current customRoomId
 * - Render areas with customizable visual options
 * - Poll for area updates
 * - Cache area data for performance
 * - Provide a clean API for integration
 */
export class VirtualRoomAreaService {
  private static instance: VirtualRoomAreaService | null = null
  
  // Configuration
  private config: VirtualRoomAreaServiceConfig = {
    enabled: true,
    autoLoad: true,
    renderOnLoad: true, // Enable rendering by default for players
    pollingInterval: EDITOR_AREA_POLL_INTERVAL_MS,
    maxAreasPerRoom: 50,
    cacheDuration: 5 * 60 * 1000, // 5 minutes
    defaultRenderingOptions: {
      alpha: 0,
      strokeWidth: 2,
      strokeColor: '#ffffff',
      fillColor: undefined,
    },
  }

  // State
  private state: VirtualRoomAreaServiceState = {
    isInitialized: false,
    isEnabled: false,
    isRendering: true, // Enable rendering by default since renderOnLoad is true
    currentCustomRoomId: null,
    currentMapId: null,
    loadedAreas: new Map(),
    renderingOptions: { ...this.config.defaultRenderingOptions },
    lastPollTime: null,
    error: null
  }

  // Internal state
  private scene: Phaser.Scene | null = null
  private pollingTimer: NodeJS.Timeout | null = null
  private pollInFlight = false
  // The one delayed reload after a failed tile or room-name read, and the room it was spent on.
  private areaLoadRetryTimer: ReturnType<typeof setTimeout> | null = null
  private areaLoadRetriedFor: string | null = null
  private areaGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map()
  private eventListeners: Map<VirtualRoomAreaEvent, Set<Function>> = new Map()
  private cacheExpiry: Map<string, number> = new Map()
  private areaCache: Map<string, VirtualRoomArea | null> = new Map()
  private borderRendererInitialized: boolean = false

  // Hover tooltip state
  private hoverLabel: Phaser.GameObjects.Text | null = null
  private hoveredAreaId: string | null = null
  private hoverDismissedAreaId: string | null = null
  private hoverTimer: ReturnType<typeof setTimeout> | null = null
  private pointerMoveHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null
  private pointerOutHandler: (() => void) | null = null
  private hoverLabelPointerDownHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null
  private hoverLabelPointerUpHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null
  private hoverLabelPointerDownPos: { x: number; y: number } | null = null
  private pendingFirstTap: { areaId: string; x: number; y: number; timestamp: number } | null = null

  // Precomputed tile→area index for O(1) hover lookups
  private tileIndex: Map<string, VirtualRoomArea> = new Map()

  /**
   * Get singleton instance
   */
  public static getInstance(): VirtualRoomAreaService {
    if (!VirtualRoomAreaService.instance) {
      VirtualRoomAreaService.instance = new VirtualRoomAreaService()
    }
    return VirtualRoomAreaService.instance
  }

  /**
   * Clear resolved areas cache.
   */
  public clearAreaCache(): void {
    this.areaCache.clear()
  }

  /**
   * Initialize the service with a Phaser scene
   */
  public async initialize(scene: Phaser.Scene, customRoomId: string): Promise<void> {
    try {
      // Bug 15 fix: Clean up areas from previous customRoomId to prevent memory growth
      const previousCustomRoomId = this.state.currentCustomRoomId
      if (previousCustomRoomId && previousCustomRoomId !== customRoomId) {
        this.state.loadedAreas.delete(previousCustomRoomId)
        this.cacheExpiry.delete(previousCustomRoomId)
        this.clearAreaGraphics(previousCustomRoomId)
        this.clearAreaCache()
        this.cancelAreaLoadRetry()
      }

      this.scene = scene
      this.state.currentCustomRoomId = customRoomId
      this.state.currentMapId = currentMapId()
      this.state.isInitialized = true
      this.state.isEnabled = this.config.enabled
      
      // Initialize border renderer
      virtualRoomAreaBorderRenderer.setScene(scene)
      virtualRoomAreaBorderRenderer.setEnabled(true) // Enable border rendering by default
      this.borderRendererInitialized = true

      // Tear down any previous hover tooltip before setting up a new one
      this.destroyHoverTooltip()
      this.setupHoverTooltip()
      
      // Load areas if auto-load is enabled
      if (this.config.autoLoad) {
        await this.loadAreasForCustomRoomId(customRoomId)
      }
      
      // Only map editor rooms poll (SC-422). Other rooms load their VRs on join and get edits by
      // restarting, so their browsers never see a VR the server room doesn't have yet.
      if (this.config.enabled && store.getState().room.isMapEditorRoom) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
      
      this.emitEvent(VirtualRoomAreaEvent.STATE_CHANGED, this.state)
      
    } catch (error) {
      this.handleError('Failed to initialize service', error)
      throw error
    }
  }

  /**
   * Loads the areas of the room's map, kept under the room's customRoomId. A map without VRs
   * has no areas; there is no fallback map or template (SC-422).
   */
  public async loadAreasForCustomRoomId(customRoomId: string): Promise<VirtualRoomArea[]> {
    try {
      console.log(`[VirtualRoomAreaService] === VIRTUAL ROOM AREAS LOADING PIPELINE ===`)
      console.log(`[VirtualRoomAreaService] Starting pipeline for customRoomId: ${customRoomId}`)
      
      // Check cache first
      const cached = this.getCachedAreas(customRoomId)
      if (cached) {
        console.log(`[VirtualRoomAreaService] ✅ Found cached areas for customRoomId: ${customRoomId}`)
        if (customRoomId === this.state.currentCustomRoomId) {
          this.rebuildTileIndex()
        }
        // Auto-render cached areas if configured
        if (this.config.renderOnLoad && this.state.isRendering) {
          this.renderAreas(customRoomId)
        }

        return cached
      }
      
      this.clearAreaCache()

      // A failed read is not "no tiles" or "no names". What could be read is shown, but it is not
      // cached (it would stay wrong for the cache duration) and one delayed reload is scheduled.
      let areas: VirtualRoomArea[] = []
      let loadFailed = false
      const alreadyLoaded = this.state.loadedAreas.get(customRoomId)
      try {
        const loaded = await this.loadAreasForMap(this.state.currentMapId)
        areas = loaded.areas
        loadFailed = loaded.namesFailed
      } catch (tileError) {
        loadFailed = true
        console.error(`[VirtualRoomAreaService] Could not read tiles for map: ${this.state.currentMapId}`, tileError)
      }
      // Only the room we are in may touch the retry: a load that finishes after a room switch
      // must neither cancel nor take the slot of the new room's retry.
      if (customRoomId === this.state.currentCustomRoomId) {
        if (loadFailed) this.scheduleAreaLoadRetry(customRoomId)
        else this.cancelAreaLoadRetry()
      }
      // A failed poll must not blank what the editor already shows. The fallback above (nothing, or
      // placeholder names) is only for a room with nothing loaded yet.
      if (loadFailed && alreadyLoaded && alreadyLoaded.length > 0) areas = alreadyLoaded
      if (!loadFailed) this.cacheAreas(customRoomId, areas)
      this.state.loadedAreas.set(customRoomId, areas)
      this.rebuildTileIndex()
      if (areas.length > 0 && this.config.renderOnLoad && this.state.isRendering) {
        this.renderAreas(customRoomId)
      }
      this.emitEvent(VirtualRoomAreaEvent.AREAS_LOADED, { customRoomId, areas })
      return areas

    } catch (error) {
      console.error(`[VirtualRoomAreaService] ❌ PIPELINE ERROR: Failed to load areas for customRoomId: ${customRoomId}`, error)
      this.handleError(`Failed to load areas for customRoomId: ${customRoomId}`, error)
      throw error
    }
  }

  /**
   * The map's VRs that have tiles, from one tile query and one VR query.
   * Throws when the tile read fails (TileSyncManager would otherwise answer [], which reads as an
   * empty map). A failed VR query does not throw: the areas keep placeholder names and
   * `namesFailed` is set. It never falls back to one query per room, which would turn one
   * Back4App failure into a burst of requests.
   */
  private async loadAreasForMap(
    mapId: string | null
  ): Promise<{ areas: VirtualRoomArea[]; namesFailed: boolean }> {
    if (!mapId) return { areas: [], namesFailed: false }
    const [tiles, rooms] = await Promise.all([
      tileSyncManager.loadTilesForMap(mapId, { throwOnError: true }),
      this.loadRoomsForMap(mapId)
    ])
    return {
      areas: this.processTilesIntoAreas(tiles, rooms ?? []),
      namesFailed: rooms === null
    }
  }

  /** null when the query failed. */
  private async loadRoomsForMap(mapId: string): Promise<VirtualRoomData[] | null> {
    try {
      return await virtualRoomService.getRoomsByMapId(mapId)
    } catch (error) {
      console.error(`[VirtualRoomAreaService] Could not read VRs for map: ${mapId}`, error)
      return null
    }
  }

  /** Schedules the one reload for a failed read; a second failure for the same room does not. */
  private scheduleAreaLoadRetry(customRoomId: string): void {
    if (this.areaLoadRetryTimer || this.areaLoadRetriedFor === customRoomId) return
    this.areaLoadRetriedFor = customRoomId
    this.areaLoadRetryTimer = setTimeout(() => {
      this.areaLoadRetryTimer = null
      if (this.state.currentCustomRoomId !== customRoomId) return
      this.cacheExpiry.delete(customRoomId)
      this.loadAreasForCustomRoomId(customRoomId).catch(() => undefined)
    }, AREA_LOAD_RETRY_DELAY_MS)
  }

  private cancelAreaLoadRetry(): void {
    if (this.areaLoadRetryTimer) clearTimeout(this.areaLoadRetryTimer)
    this.areaLoadRetryTimer = null
    this.areaLoadRetriedFor = null
  }

  private processTilesIntoAreas(
    tiles: Array<{ roomId: string; tileX: number; tileY: number }>,
    rooms: VirtualRoomData[]
  ): VirtualRoomArea[] {
    const roomsById = new Map(rooms.map(room => [room.roomId, room]))
    const areasByRoom = new Map<string, VirtualRoomArea>()

    tiles.forEach(tile => {
      if (!areasByRoom.has(tile.roomId)) {
        const room = roomsById.get(tile.roomId)
        areasByRoom.set(tile.roomId, {
          roomId: tile.roomId,
          roomName: room?.name || `Room ${tile.roomId}`,
          kind: room?.kind || 'meeting',
          color: room?.color || this.generateRoomColor(tile.roomId),
          tiles: [],
          bounds: { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
          center: { x: 0, y: 0 },
          tileCount: 0,
          properties: {
            uniqueRoomId: room?.properties?.uniqueRoomId || tile.roomId,
            proximityDistance: room?.properties?.proximityDistance ?? DEFAULT_PROXIMITY_FALLBACK_PX
          },
          metadata: room?.metadata
        })
      }

      const area = areasByRoom.get(tile.roomId)!
      area.tiles.push({ x: tile.tileX, y: tile.tileY })
      area.tileCount++
      area.bounds.minX = Math.min(area.bounds.minX, tile.tileX)
      area.bounds.minY = Math.min(area.bounds.minY, tile.tileY)
      area.bounds.maxX = Math.max(area.bounds.maxX, tile.tileX)
      area.bounds.maxY = Math.max(area.bounds.maxY, tile.tileY)
    })

    return Array.from(areasByRoom.values()).map(area => ({
      ...area,
      center: {
        x: (area.bounds.minX + area.bounds.maxX) / 2,
        y: (area.bounds.minY + area.bounds.maxY) / 2
      }
    }))
  }

  /**
   * Render areas for a specific customRoomId
   */
  public renderAreas(customRoomId: string): void {
    if (!this.scene || !this.state.isRendering) {
      return
    }
    
    try {

      
      // Clear existing graphics for this customRoomId
      this.clearAreaGraphics(customRoomId)
      
      const areas = this.state.loadedAreas.get(customRoomId)
      if (!areas || areas.length === 0) {
        return
      }
      
      areas.forEach(area => {
        this.renderSingleArea(area)
      })
      
      // Render borders for all areas
      if (this.borderRendererInitialized) {
        virtualRoomAreaBorderRenderer.paintAreaBorders(areas)
      }
      
      this.emitEvent(VirtualRoomAreaEvent.AREAS_RENDERED, { customRoomId, areas })
      
    } catch (error) {
      this.handleError(`Failed to render areas for customRoomId: ${customRoomId}`, error)
    }
  }

  /**
   * Hide all rendered areas
   */
  public hideAreas(): void {
    if (!this.scene) return
    
    try {

      
      // Clear all graphics
      this.areaGraphics.forEach(graphics => {
        graphics.destroy()
      })
      this.areaGraphics.clear()
      
      // Clear borders
      if (this.borderRendererInitialized) {
        virtualRoomAreaBorderRenderer.clearAllBorders()
      }
      
      this.emitEvent(VirtualRoomAreaEvent.AREAS_HIDDEN)
      
    } catch (error) {
      this.handleError('Failed to hide areas', error)
    }
  }

  /**
   * Toggle area rendering
   */
  public toggleRendering(): boolean {
    const newState = !this.state.isRendering
    this.state.isRendering = newState
    
    if (newState) {
      // Show areas
      if (this.state.currentCustomRoomId) {
        this.renderAreas(this.state.currentCustomRoomId)
      }
    } else {
      // Hide areas
      this.hideAreas()
    }
    
    this.emitEvent(VirtualRoomAreaEvent.STATE_CHANGED, this.state)
    
    return newState
  }

  /**
   * Update rendering options
   */
  public updateRenderingOptions(options: Partial<AreaRenderingOptions>): void {
    this.state.renderingOptions = { ...this.state.renderingOptions, ...options }
    
    // Re-render if currently rendering
    if (this.state.isRendering && this.state.currentCustomRoomId) {
      this.renderAreas(this.state.currentCustomRoomId)
    }
    
    this.emitEvent(VirtualRoomAreaEvent.STATE_CHANGED, this.state)
  }

  /**
   * Toggle border rendering
   */
  public toggleBorderRendering(): boolean {
    if (!this.borderRendererInitialized) {
      return false
    }
    
    const currentState = virtualRoomAreaBorderRenderer.isBorderRenderingEnabled()
    const newState = !currentState
    
    virtualRoomAreaBorderRenderer.setEnabled(newState)
    
    // If enabling and we have areas loaded, render borders
    if (newState && this.state.currentCustomRoomId) {
      const areas = this.state.loadedAreas.get(this.state.currentCustomRoomId)
      if (areas && areas.length > 0) {
        virtualRoomAreaBorderRenderer.paintAreaBorders(areas)
      }
    }
    

    return newState
  }

  /**
   * Check if border rendering is enabled
   */
  public isBorderRenderingEnabled(): boolean {
    return this.borderRendererInitialized && virtualRoomAreaBorderRenderer.isBorderRenderingEnabled()
  }

  /**
   * Update border renderer options
   */
  public updateBorderOptions(options: Partial<{
    borderWidth: number
    borderOpacity: number
    depth: number
  }>): void {
    if (!this.borderRendererInitialized) {
      return
    }
    
    virtualRoomAreaBorderRenderer.updateOptions(options)
    
    // Re-render borders if currently rendering
    if (this.state.isRendering && this.state.currentCustomRoomId) {
      const areas = this.state.loadedAreas.get(this.state.currentCustomRoomId)
      if (areas && areas.length > 0) {
        virtualRoomAreaBorderRenderer.paintAreaBorders(areas)
      }
    }
  }

  /**
   * Force render areas and borders (for debugging)
   */
  public forceRender(): void {
    if (!this.state.currentCustomRoomId) {
      return
    }
    this.state.isRendering = true
    this.renderAreas(this.state.currentCustomRoomId)
  }

  /**
   * Debug method to check service state
   */
  public debugState(): void {
    console.log('🔍 [VirtualRoomAreaService] Debug State:')
    console.log('  - isInitialized:', this.state.isInitialized)
    console.log('  - isEnabled:', this.state.isEnabled)
    console.log('  - currentCustomRoomId:', this.state.currentCustomRoomId)
    console.log('  - loadedAreas count:', this.state.loadedAreas.size)

    this.state.loadedAreas.forEach((areas, roomId) => {
      console.log(`  - Room ${roomId}: ${areas.length} areas`)
      areas.forEach((area, index) => {
        console.log(`    Area ${index + 1}:`, {
          roomId: area.roomId,
          roomName: area.roomName,
          proximityDistance: area.properties?.proximityDistance,
          tileCount: area.tileCount,
          bounds: area.bounds
        })
      })
    })
  }

  /**
   * Get current service state
   */
  public getState(): VirtualRoomAreaServiceState {
    return { ...this.state }
  }

  /**
   * Get areas for current customRoomId
   */
  public getCurrentAreas(): VirtualRoomArea[] {
    if (!this.state.currentCustomRoomId) return []
    return this.state.loadedAreas.get(this.state.currentCustomRoomId) || []
  }

  /**
   * Check if the service is initialized
   */
  public isInitialized(): boolean {
    return this.state.isInitialized
  }

  /**
   * The area the player at sprite position (playerX, playerY) is in, or null.
   *
   * Uses the rule shared with the server (types/virtualRoomMembership.ts): the avatar's collision
   * body must cover 30% of its area with a room's tiles to enter, or 10% to stay in previousArea.
   * The server resolves the same position with the same function, so an entry the client declares
   * is one the server agrees with.
   *
   * @param previousArea The area the player is currently in, for hysteresis (prevents flickering)
   */
  public isPlayerInArea(
    playerX: number, 
    playerY: number, 
    customRoomId?: string,
    previousArea?: VirtualRoomArea | null
  ): VirtualRoomArea | null {
    const targetCustomRoomId = customRoomId || this.state.currentCustomRoomId
    if (!targetCustomRoomId) return null

    // Cache lookup based on inputs
    const cacheKey = `${playerX}_${playerY}_${targetCustomRoomId}_${previousArea ? previousArea.roomId : ''}`
    if (this.areaCache.has(cacheKey)) {
      return this.areaCache.get(cacheKey) || null
    }
    
    const areas = this.state.loadedAreas.get(targetCustomRoomId)
    if (!areas || areas.length === 0) {
      this.areaCache.set(cacheKey, null)
      return null
    }

    const roomId = resolveVirtualRoomIdAtBody(
      playerX,
      playerY,
      (tileX, tileY) => areas.find((area) => this.isPointInArea(tileX, tileY, area))?.roomId,
      previousArea?.roomId ?? null
    )
    const result = roomId ? areas.find((area) => area.roomId === roomId) ?? null : null
    this.areaCache.set(cacheKey, result)
    return result
  }

  /**
   * Add event listener
   */
  public on(event: VirtualRoomAreaEvent, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(callback)
  }

  /**
   * Remove event listener
   */
  public off(event: VirtualRoomAreaEvent, callback: Function): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.delete(callback)
    }
  }

  /**
   * Cleanup and destroy the service
   */
  public destroy(): void {

    
    // Stop polling
    this.stopPolling()
    this.cancelAreaLoadRetry()

    // Clean up hover tooltip
    this.destroyHoverTooltip()

    // Clear graphics
    this.hideAreas()
    
    // Clear cache
    this.state.loadedAreas.clear()
    this.tileIndex.clear()
    this.cacheExpiry.clear()
    this.clearAreaCache()
    
    // Clear event listeners
    this.eventListeners.clear()
    
    // Reset state
    this.state = {
      isInitialized: false,
      isEnabled: false,
      isRendering: false,
      currentCustomRoomId: null,
      currentMapId: null,
      loadedAreas: new Map(),
      renderingOptions: { ...this.config.defaultRenderingOptions },
      lastPollTime: null,
      error: null
    }
    
    // Clear instance
    VirtualRoomAreaService.instance = null
  }

  // Private methods

  private renderSingleArea(area: VirtualRoomArea): void {
    if (!this.scene) return
    
    const options = this.state.renderingOptions
    const graphics = this.scene.add.graphics()
    
    // Set depth to render behind players but above background
    graphics.setDepth(1)
    
    // Parse color
    const color = this.parseColor(area.color)
    const fillColor = options.fillColor ? this.parseColor(options.fillColor) : color
    
    // Fill tiles (outline is handled by VirtualRoomAreaBorderRenderer)
    this.renderAreaTiles(graphics, area.tiles, fillColor, options.alpha || 0)
    
    // Store graphics reference
    const graphicsKey = `${area.roomId}_${this.state.currentCustomRoomId}`
    this.areaGraphics.set(graphicsKey, graphics)
  }

  private renderAreaTiles(graphics: Phaser.GameObjects.Graphics, tiles: Array<{ x: number; y: number }>, fillColor: number, alpha: number): void {
    // Render each tile as a small rectangle
    tiles.forEach(tile => {
      const x = tile.x * TILE_SIZE
      const y = tile.y * TILE_SIZE

      if (alpha > 0) {
        graphics.fillStyle(fillColor, alpha)
        graphics.fillRect(x, y, TILE_SIZE, TILE_SIZE)
      }
    })
  }

  /**
   * Rebuild the tile→area index from the currently loaded areas.
   * Called whenever areas are loaded or updated.
   */
  private rebuildTileIndex(): void {
    this.tileIndex.clear()
    if (!this.state.currentCustomRoomId) return
    const areas = this.state.loadedAreas.get(this.state.currentCustomRoomId)
    if (!areas) return
    for (const area of areas) {
      for (const tile of area.tiles) {
        this.tileIndex.set(`${tile.x},${tile.y}`, area)
      }
    }
  }

  /**
   * O(1) lookup of which area contains a given tile coordinate.
   */
  private getAreaAtTile(tileX: number, tileY: number): VirtualRoomArea | null {
    return this.tileIndex.get(`${tileX},${tileY}`) ?? null
  }

  /**
   * Set up the pointer-move listener that shows/hides the hover tooltip.
   */
  private setupHoverTooltip(): void {
    if (!this.scene) return

    // Create the reusable label (hidden initially)
    this.hoverLabel = this.scene.add.text(0, 0, '', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      backgroundColor: '#333333',
      padding: { x: 6, y: 3 }
    })
    this.hoverLabel.setOrigin(0.5, 1)
    this.hoverLabel.setDepth(10000)
    this.hoverLabel.setVisible(false)

    this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
      if (!this.scene) return

      const worldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y)
      const tileX = Math.floor(worldPoint.x / TILE_SIZE)
      const tileY = Math.floor(worldPoint.y / TILE_SIZE)

      const area = this.getAreaAtTile(tileX, tileY)

      if (area) {
        if (this.hoveredAreaId !== area.roomId) {
          // Entered a new area
          this.hoveredAreaId = area.roomId

          // A pending first tap belongs to whichever room was hovered when it landed — hovering
          // away and back must not let it combine with a tap on a different room into a double-tap.
          if (this.pendingFirstTap && this.pendingFirstTap.areaId !== area.roomId) {
            this.pendingFirstTap = null
          }

          if (this.hoverDismissedAreaId === area.roomId) {
            // Already dismissed for this area — stay hidden
            return
          }

          this.hoverLabel!.setText(area.roomName)
          this.hoverLabel!.setVisible(true)

          // Reset auto-hide timer
          if (this.hoverTimer) clearTimeout(this.hoverTimer)
          this.hoverTimer = setTimeout(() => {
            this.hoverDismissedAreaId = this.hoveredAreaId
            this.hoverLabel!.setVisible(false)
            this.hoverTimer = null
          }, 3000)
        }
        // Position above the pointer in world coords
        this.hoverLabel!.setPosition(worldPoint.x, worldPoint.y - 20)
      } else {
        if (this.hoveredAreaId !== null) {
          this.hoveredAreaId = null
          this.hoverDismissedAreaId = null
          this.hoverLabel!.setVisible(false)
          if (this.hoverTimer) {
            clearTimeout(this.hoverTimer)
            this.hoverTimer = null
          }
        }
      }
    }

    this.pointerOutHandler = () => {
      if (this.hoveredAreaId !== null) {
        this.hoveredAreaId = null
        this.hoverDismissedAreaId = null
        this.hoverLabel!.setVisible(false)
        if (this.hoverTimer) {
          clearTimeout(this.hoverTimer)
          this.hoverTimer = null
        }
      }
    }

    this.scene.input.on('pointermove', this.pointerMoveHandler)
    this.scene.input.on('pointerout', this.pointerOutHandler)
    this.scene.input.on('gameout', this.pointerOutHandler)

    // Click-to-teleport (SC-396): reuses the hover tooltip's already-tracked hoveredAreaId rather
    // than a separate hit-test. Gesture is disambiguated from a camera-drag pan by pointer distance
    // between down/up, not by the scene's isDraggingCamera flag — that flag flips true on every
    // drag-button pointerdown regardless of whether the pointer ever actually moves, so it can't
    // tell a click from the start of a drag by itself.
    this.hoverLabelPointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      this.hoverLabelPointerDownPos = { x: pointer.x, y: pointer.y }
    }
    this.hoverLabelPointerUpHandler = (pointer: Phaser.Input.Pointer) => {
      const downPos = this.hoverLabelPointerDownPos
      this.hoverLabelPointerDownPos = null
      if (!downPos || !this.hoveredAreaId) return
      const dx = pointer.x - downPos.x
      const dy = pointer.y - downPos.y
      if (dx * dx + dy * dy > HOVER_LABEL_CLICK_MOVE_THRESHOLD_PX * HOVER_LABEL_CLICK_MOVE_THRESHOLD_PX) return

      // Require a double-tap, not a single tap, so a stray click while panning the map never
      // fires an unwanted teleport. A qualifying second tap must land on the same room, within
      // the time and distance window of the first.
      const pending = this.pendingFirstTap
      const isQualifyingSecondTap =
        !!pending &&
        pending.areaId === this.hoveredAreaId &&
        Date.now() - pending.timestamp <= DOUBLE_TAP_WINDOW_MS &&
        (pointer.x - pending.x) ** 2 + (pointer.y - pending.y) ** 2 <=
          DOUBLE_TAP_MOVE_THRESHOLD_PX * DOUBLE_TAP_MOVE_THRESHOLD_PX

      if (!isQualifyingSecondTap) {
        this.pendingFirstTap = { areaId: this.hoveredAreaId, x: pointer.x, y: pointer.y, timestamp: Date.now() }
        return
      }

      this.pendingFirstTap = null

      // The hover label follows the cursor everywhere inside a room's tile area (see
      // pointerMoveHandler above), so "double-click the label" and "double-click anywhere in the
      // room — a chair, another player's sprite, empty floor" are the same gesture. Double-clicking
      // the room you're already standing in would otherwise teleport you to its own door, a
      // surprise "kick outside" — no-op instead, the same way a doubled TELEPORT_REQUEST is
      // silently dropped server-side rather than acted on twice.
      const myPlayerId = (this.scene as any)?.myPlayer?.playerId
      const myCurrentAreaId = myPlayerId ? virtualRoomAreaDetectionService.getPlayerCurrentArea(myPlayerId)?.roomId : undefined
      if (myCurrentAreaId === this.hoveredAreaId) return

      requestTeleport({ type: 'room', roomId: this.hoveredAreaId })
    }
    this.scene.input.on('pointerdown', this.hoverLabelPointerDownHandler)
    this.scene.input.on('pointerup', this.hoverLabelPointerUpHandler)
  }

  /**
   * Tear down the hover tooltip and its listener.
   */
  private destroyHoverTooltip(): void {
    if (this.scene) {
      if (this.pointerMoveHandler) {
        this.scene.input.off('pointermove', this.pointerMoveHandler)
      }
      if (this.pointerOutHandler) {
        this.scene.input.off('pointerout', this.pointerOutHandler)
        this.scene.input.off('gameout', this.pointerOutHandler)
      }
      if (this.hoverLabelPointerDownHandler) {
        this.scene.input.off('pointerdown', this.hoverLabelPointerDownHandler)
      }
      if (this.hoverLabelPointerUpHandler) {
        this.scene.input.off('pointerup', this.hoverLabelPointerUpHandler)
      }
    }
    this.pointerMoveHandler = null
    this.pointerOutHandler = null
    this.hoverLabelPointerDownHandler = null
    this.hoverLabelPointerUpHandler = null
    this.hoverLabelPointerDownPos = null
    this.pendingFirstTap = null
    if (this.hoverLabel) {
      this.hoverLabel.destroy()
      this.hoverLabel = null
    }
    this.hoveredAreaId = null
    this.hoverDismissedAreaId = null
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer)
      this.hoverTimer = null
    }
  }

  private clearAreaGraphics(customRoomId: string): void {
    // Bug 3 fix: Use exact suffix matching instead of includes() to prevent
    // matching unrelated room IDs (e.g. "room1" matching "room10")
    // Key format is: `${area.roomId}_${customRoomId}`
    const suffix = `_${customRoomId}`
    
    // Clear graphics for this customRoomId
    this.areaGraphics.forEach((graphics, key) => {
      if (key.endsWith(suffix)) {
        graphics.destroy()
        this.areaGraphics.delete(key)
      }
    })
    
  }

  private startPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
    }
    
    this.pollingTimer = setInterval(() => {
      this.pollForUpdates()
    }, this.config.pollingInterval)
    

  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null

    }
  }

  private async pollForUpdates(): Promise<void> {
    if (!this.state.currentCustomRoomId) return
    // A tick is skipped, not queued, while the tab is hidden or the last read is still running.
    if (this.pollInFlight) return
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return

    this.pollInFlight = true
    try {
      // Bug 12 fix: Clear cache AFTER loading to avoid a data gap where
      // isPlayerInArea() returns null because cached areas were deleted
      // before new ones were loaded.
      // The loadAreasForCustomRoomId method checks cache first, so we need
      // to clear it to force a fresh load, but we do it atomically by
      // invalidating the expiry only (not the loaded areas themselves).
      this.cacheExpiry.delete(this.state.currentCustomRoomId)
      // The tile cache outlives a poll interval, so a poll that kept it would never see new tiles.
      if (this.state.currentMapId) tileSyncManager.clearLoadedTilesCache(this.state.currentMapId)
      
      // Reload areas (will skip cache since expiry was cleared)
      await this.loadAreasForCustomRoomId(this.state.currentCustomRoomId)
      
      this.state.lastPollTime = Date.now()
      
    } catch (error) {
      this.handleError('Failed to poll for updates', error)
    } finally {
      this.pollInFlight = false
    }
  }

  private getCachedAreas(customRoomId: string): VirtualRoomArea[] | null {
    const expiry = this.cacheExpiry.get(customRoomId)
    if (!expiry || expiry < Date.now()) {
      return null
    }
    
    return this.state.loadedAreas.get(customRoomId) || null
  }

  private cacheAreas(customRoomId: string, areas: VirtualRoomArea[]): void {
    this.cacheExpiry.set(customRoomId, Date.now() + this.config.cacheDuration)
  }

  private clearCache(customRoomId: string): void {
    this.cacheExpiry.delete(customRoomId)
  }

  /**
   * Check if a point (tile coordinate) is inside an area
   */
  private isPointInArea(tileX: number, tileY: number, area: VirtualRoomArea): boolean {
    return area.tiles.some(tile => tile.x === tileX && tile.y === tileY)
  }

  private generateRoomColor(roomId: string): string {
    // Generate a consistent color based on room ID
    const hash = this.hashString(roomId)
    const hue = hash % 360
    return `hsl(${hue}, 70%, 50%)`
  }

  private hashString(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  private parseColor(color: string): number {
    if (!color) return 0xffffff // Default white
    
    // Handle hex colors
    if (color.startsWith('#')) {
      return parseInt(color.slice(1), 16)
    }
    
    // Handle HSL colors
    if (color.startsWith('hsl')) {
      const match = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
      if (match) {
        const h = parseInt(match[1])
        const s = parseInt(match[2])
        const l = parseInt(match[3])
        return Phaser.Display.Color.HSVToRGB(h / 360, s / 100, l / 100).color
      }
    }
    
    // Handle RGB colors (r,g,b format)
    if (color.includes(',')) {
      const rgb = color.split(',').map(Number)
      if (rgb.length >= 3) {
        return Phaser.Display.Color.GetColor(rgb[0], rgb[1], rgb[2])
      }
    }
    
    // Handle named colors
    const namedColors: { [key: string]: number } = {
      'red': 0xff0000,
      'green': 0x00ff00,
      'blue': 0x0000ff,
      'yellow': 0xffff00,
      'cyan': 0x00ffff,
      'magenta': 0xff00ff,
      'white': 0xffffff,
      'black': 0x000000,
      'gray': 0x808080,
      'orange': 0xffa500,
      'purple': 0x800080,
      'pink': 0xffc0cb
    }
    
    const lowerColor = color.toLowerCase().trim()
    if (namedColors[lowerColor]) {
      return namedColors[lowerColor]
    }
    

    return 0xffffff // Default white
  }

  private emitEvent(event: VirtualRoomAreaEvent, data?: any): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          // Silently handle event listener errors
        }
      })
    }
  }

  private handleError(message: string, error: any): void {
    const errorMessage = `${message}: ${error?.message || error}`
    console.error(`[VirtualRoomAreaService] ${errorMessage}`)
    this.state.error = errorMessage
    this.emitEvent(VirtualRoomAreaEvent.ERROR, { message: errorMessage, error })
  }
}

// Export singleton instance
export const virtualRoomAreaService = VirtualRoomAreaService.getInstance()

// Expose debug methods globally for console access
if (typeof window !== 'undefined') {
  (window as any).debugVirtualRoomAreas = () => {
    virtualRoomAreaService.debugState()
  }
  (window as any).forceRenderVirtualRoomAreas = () => {
    virtualRoomAreaService.forceRender()
  }

  // Debug function to test virtual room proximity
  (window as any).testVirtualRoomProximity = (player1X: number, player1Y: number, player2X: number, player2Y: number) => {
    const myPlayer = { x: player1X, y: player1Y, playerId: 'test1' }
    const otherPlayer = { x: player2X, y: player2Y, playerId: 'test2' }

    // Import the proximity helper
    const { canPlayersCommunicate } = require('./VirtualRoomProximityHelper')

    // Test with different fallback distances
    const distances = [50, 100, 200, 500]

    console.log('🔍 [VirtualRoomProximity] Testing proximity between:')
    console.log(`  Player 1: (${player1X}, ${player1Y})`)
    console.log(`  Player 2: (${player2X}, ${player2Y})`)

    distances.forEach(distance => {
      const canCommunicate = canPlayersCommunicate(myPlayer, otherPlayer, distance)
      console.log(`  Distance ${distance}px: ${canCommunicate ? '✅ CAN communicate' : '❌ CANNOT communicate'}`)
    })

    // Get current areas and their proximity distances
    const areas = virtualRoomAreaService.getCurrentAreas()
    console.log(`  Current areas: ${areas.length}`)
    areas.forEach((area, index) => {
      console.log(`    Area ${index + 1}: ${area.roomName} - proximityDistance: ${area.properties?.proximityDistance}`)
    })
  }

} 